import React, { useState } from 'react';
import { X, User, Mail, Phone, Calendar, CheckCircle, XCircle, FileText } from 'lucide-react';
import { EmployeeApproval } from '../../types';
import { useToast } from '../../contexts/ToastContext';

interface PhotoVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: EmployeeApproval | null;
  onApprove: (employeeId: string) => Promise<boolean>;
  onReject: (employeeId: string, reason: string) => Promise<boolean>;
}

const PhotoVerificationModal: React.FC<PhotoVerificationModalProps> = ({
  isOpen,
  onClose,
  employee,
  onApprove,
  onReject,
}) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  if (!isOpen || !employee) return null;

  const handleApprove = async () => {
    if (loading) return;
    
    setLoading(true);
    toast.info('Approving photo verification...');
    
    const success = await onApprove(employee.id);
    setLoading(false);
    
    if (success) {
      toast.success(`${employee.user.firstName} ${employee.user.lastName}'s photo approved successfully!`);
      onClose();
    } else {
      toast.error('Failed to approve photo. Please try again.');
    }
  };

  const handleReject = async () => {
    if (loading || !rejectionReason.trim()) return;
    
    setLoading(true);
    toast.info('Processing rejection...');
    
    const success = await onReject(employee.id, rejectionReason);
    setLoading(false);
    
    if (success) {
      toast.success(`${employee.user.firstName} ${employee.user.lastName}'s photo rejected`);
      setRejectionReason('');
      setShowRejectForm(false);
      onClose();
    } else {
      toast.error('Failed to reject photo. Please try again.');
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Photo Verification</h2>
            <p className="text-sm text-gray-600">Review and verify employee identity</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Employee Photo */}
          <div className="flex justify-center">
            <div className="relative">
              {employee.user.profileImage ? (
                <img
                  src={employee.user.profileImage}
                  alt={`${employee.user.firstName} ${employee.user.lastName}`}
                  className="w-48 h-48 rounded-lg object-cover border-4 border-primary-100"
                />
              ) : (
                <div className="w-48 h-48 rounded-lg bg-gray-200 flex items-center justify-center border-4 border-primary-100">
                  <User className="h-24 w-24 text-gray-400" />
                </div>
              )}
            </div>
          </div>

          {/* Employee Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Employee Information</h3>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <User className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Full Name</p>
                    <p className="text-sm font-medium text-gray-900">
                      {employee.user.firstName} {employee.user.lastName}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-sm font-medium text-gray-900">{employee.user.email}</p>
                  </div>
                </div>

                {employee.user.phone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="text-sm font-medium text-gray-900">{employee.user.phone}</p>
                    </div>
                  </div>
                )}

                {employee.user.dateOfBirth && (
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Date of Birth</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(employee.user.dateOfBirth)}
                      </p>
                    </div>
                  </div>
                )}

                {employee.user.position && (
                  <div className="flex items-center space-x-3">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Position</p>
                      <p className="text-sm font-medium text-gray-900">{employee.user.position}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Verification Status</h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 mb-1">KYC Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    employee.kycStatus === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                    employee.kycStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {employee.kycStatus}
                  </span>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Photo Verification Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    employee.photoVerificationStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
                    employee.photoVerificationStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {employee.photoVerificationStatus}
                  </span>
                </div>

                {employee.kycData && employee.kycData.dojahVerificationComplete && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <p className="text-sm font-medium text-green-900">Dojah Verification Complete</p>
                    </div>
                    <p className="text-xs text-green-700 mt-1">
                      Identity verified on {formatDate(employee.kycData.dojahVerifiedAt)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Rejection Form */}
          {showRejectForm && (
            <div className="space-y-4 p-4 bg-red-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-900">
                Rejection Reason *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Please provide a detailed reason for rejection..."
              />
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>

          {!showRejectForm ? (
            <>
              <button
                onClick={() => setShowRejectForm(true)}
                className="btn-danger"
                disabled={loading}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </button>
              <button
                onClick={handleApprove}
                className="btn-success"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve Photo
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectionReason('');
                }}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel Rejection
              </button>
              <button
                onClick={handleReject}
                className="btn-danger"
                disabled={loading || !rejectionReason.trim()}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Rejecting...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                    Confirm Rejection
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoVerificationModal;

